// File: edgelexapp/webelieve-frontend/src/features/dms/components/CheckinModal.tsx
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { 
  DocumentTextIcon,
  CheckIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useDocumentAPI } from '../DocumentAPI';

// Helper function to find the highest version number from a list of versions
function findHighestVersion(versions: any[]): string | null {
  if (!versions || versions.length === 0) return null;
  
  let highestVersion = '';
  let highestMajor = -1;
  let highestMinor = -1;
  
  for (const version of versions) {
    const versionNumber = version.version_number;
    if (!versionNumber) continue;
    
    const match = versionNumber.match(/^v(\d+)\.(\d+)$/);
    if (match) {
      const major = parseInt(match[1]);
      const minor = parseInt(match[2]);
      
      if (major > highestMajor || (major === highestMajor && minor > highestMinor)) {
        highestMajor = major;
        highestMinor = minor;
        highestVersion = versionNumber;
      }
    }
  }
  
  return highestVersion || null;
}

interface CheckinModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentId: string;
  documentTitle: string;
  currentVersion?: string;
  hasWorkingCopy?: boolean;
  lastLocalSaveAt?: string | null;
  onCheckinSuccess?: () => void;
  versionId?: string; // Optional for version-specific checkin
}

export function CheckinModal({
  isOpen,
  onClose,
  documentId,
  documentTitle,
  currentVersion = 'v1.0',
  hasWorkingCopy = false,
  lastLocalSaveAt,
  onCheckinSuccess,
  versionId
}: CheckinModalProps): React.JSX.Element | null {
  const documentAPI = useDocumentAPI();
  
  // State
  const [checkinOption, setCheckinOption] = useState<'create-version' | 'overwrite' | 'discard'>('create-version');
  const [versionComment, setVersionComment] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextVersionNumber, setNextVersionNumber] = useState('');
  const [actualCurrentVersion, setActualCurrentVersion] = useState(currentVersion);
  const canCreateVersionFromWorkingCopy = !versionId && hasWorkingCopy;
  const isReleaseOnlyFlow = !canCreateVersionFromWorkingCopy;

  React.useEffect(() => {
    if (!canCreateVersionFromWorkingCopy && checkinOption !== 'discard') {
      setCheckinOption('discard');
    }
  }, [canCreateVersionFromWorkingCopy, checkinOption]);

  // Fetch the actual version number when versionId is provided
  React.useEffect(() => {
    const fetchVersionInfo = async () => {
      if (versionId) {
        try {
          // Get version information from the versions API
          const response = await fetch(`/api/v1/dms/documents/${documentId}/versions`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('webelieve_access_token') || localStorage.getItem('access_token')}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            const version = data.versions?.find((v: any) => v.version_id === versionId);
            if (version) {
              setActualCurrentVersion(version.version_number);
              console.log(`[CheckinModal] Found version ${version.version_number} for versionId ${versionId}`);
              
              // Calculate next version based on ALL existing versions (not just the checked-out one)
              const allVersions = data.versions || [];
              const highestVersion = findHighestVersion(allVersions);
              if (highestVersion) {
                const match = highestVersion.match(/^v(\d+)\.\d+$/);
                if (match) {
                  const majorVersion = parseInt(match[1]);
                  setNextVersionNumber(`v${majorVersion + 1}.0`);
                  console.log(`[CheckinModal] Next version calculated from highest existing version ${highestVersion}: v${majorVersion + 1}.0`);
                }
              }
            }
          }
        } catch (error) {
          console.warn('[CheckinModal] Failed to fetch version info:', error);
          // Fallback to the provided currentVersion
        }
      }
    };

    fetchVersionInfo();
  }, [versionId, documentId]);

  // Fallback: Calculate next version number if not already set by the version fetch
  React.useEffect(() => {
    if (actualCurrentVersion && !nextVersionNumber) {
      const match = actualCurrentVersion.match(/^v(\d+)\.\d+$/);
      if (match) {
        const majorVersion = parseInt(match[1]);
        setNextVersionNumber(`v${majorVersion + 1}.0`);
        console.log(`[CheckinModal] Fallback: Next version calculated from current version ${actualCurrentVersion}: v${majorVersion + 1}.0`);
      }
    }
  }, [actualCurrentVersion, nextVersionNumber]);

  const handleCheckin = async () => {
    if (checkinOption === 'create-version' && canCreateVersionFromWorkingCopy && !versionComment.trim()) {
      setError('Please provide a version comment when creating a new version');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const shouldCreateVersion = canCreateVersionFromWorkingCopy && checkinOption === 'create-version';
      const options = {
        createNewVersion: shouldCreateVersion,
        versionComment: shouldCreateVersion
          ? versionComment.trim()
          : 'Checkout released without creating a new DMS version',
        forceCheckin: false,
        workingCopyData: shouldCreateVersion ? {
          content: undefined, // Will be provided by the document editor
          filePath: undefined,
          metadata: {
            sessionId: `session_${Date.now()}`,
            deviceInfo: navigator.userAgent,
            autosaveCount: 0,
          }
        } : undefined
      };

      // Use version-specific checkin if versionId is provided, otherwise use document-level
      const result = versionId 
        ? await documentAPI.checkinDocumentVersion(versionId, options)
        : await documentAPI.checkinDocument(documentId, options);
      
      if (result.success) {
        onCheckinSuccess?.();
        onClose();
      } else {
        setError('Failed to check in document');
      }
    } catch (err) {
      console.error('Check-in error:', err);
      setError(err instanceof Error ? err.message : 'Failed to check in document');
    } finally {
      setIsProcessing(false);
    }
  };



  if (!isOpen) return null;

  // Render modal using portal to escape parent modal constraints
  const modalContent = (
    <div className="fixed inset-0 z-[10000] overflow-y-auto">
      <div className="flex items-center justify-center min-h-full p-4">
        {/* Background overlay */}
        <div className="fixed inset-0 bg-gray-900 bg-opacity-80 transition-opacity lex-overlay-enter" onClick={onClose} />

        {/* Modal panel */}
        <div className="relative w-full max-w-lg bg-white dark:bg-gray-900 rounded-lg text-left overflow-hidden shadow-xl lex-modal-enter">
          {/* Header */}
          <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-lex-accent-soft sm:mx-0 sm:h-10 sm:w-10">
                <DocumentTextIcon className="h-6 w-6 text-lex-accent" />
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                  {canCreateVersionFromWorkingCopy ? 'Check In Document' : 'Release Checkout'}
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {documentTitle}
                  </p>
                  {isReleaseOnlyFlow && (
                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">
                      This unlocks the selected DMS version. It does not upload local Word edits or create a new official version.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="bg-white dark:bg-gray-800 px-4 pb-4 sm:p-6 sm:pb-4">
            {/* Current Version Info */}
            <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Current Version:</span>
                <span className="font-mono text-gray-900 dark:text-white">{actualCurrentVersion}</span>
              </div>
              {hasWorkingCopy && lastLocalSaveAt && (
                <div className="flex items-center justify-between text-sm mt-1">
                  <span className="text-gray-600 dark:text-gray-400">Last Saved:</span>
                  <span className="text-gray-900 dark:text-white">
                    {new Date(lastLocalSaveAt).toLocaleString()}
                  </span>
                </div>
              )}
            </div>

            {/* Check-in Options */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {canCreateVersionFromWorkingCopy ? 'Check-in Options' : 'Checkout Release'}
              </h4>
              
              {canCreateVersionFromWorkingCopy && (
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="checkinOption"
                    value="create-version"
                    checked={checkinOption === 'create-version'}
                    onChange={(e) => setCheckinOption(e.target.value as any)}
                    className="mt-1 h-4 w-4 text-lex-accent focus:ring-lex-accent border-gray-300"
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        Create New Version
                      </span>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                        {nextVersionNumber}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Save the current working copy as a new DMS version with a comment.
                    </p>
                  </div>
                </label>
              )}

              {/* Discard Changes */}
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="checkinOption"
                  value="discard"
                  checked={checkinOption === 'discard'}
                  onChange={(e) => setCheckinOption(e.target.value as any)}
                  className="mt-1 h-4 w-4 text-lex-accent focus:ring-lex-accent border-gray-300"
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {canCreateVersionFromWorkingCopy ? 'Release Checkout Without New Version' : 'Release Checkout'}
                  </span>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {canCreateVersionFromWorkingCopy
                      ? 'Do not create a DMS version from the working copy. This only releases the checkout lock.'
                      : 'Use this when the edited document was already saved to DMS, or when you only need to unlock the version.'}
                  </p>
                </div>
              </label>
            </div>

            {/* Version Comment */}
            {canCreateVersionFromWorkingCopy && checkinOption === 'create-version' && (
              <div className="mt-4">
                <label htmlFor="versionComment" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Version Comment *
                </label>
                <textarea
                  id="versionComment"
                  value={versionComment}
                  onChange={(e) => setVersionComment(e.target.value)}
                  placeholder="Describe the changes made in this version..."
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-lex-accent focus:border-lex-accent dark:bg-gray-700 dark:text-white"
                  rows={3}
                />
              </div>
            )}

            {!canCreateVersionFromWorkingCopy && (
              <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <div className="flex items-start space-x-2">
                  <ExclamationTriangleIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-blue-900 dark:text-blue-200">No document bytes will be saved from this modal</h4>
                    <p className="text-sm text-blue-800 dark:text-blue-300 mt-1">
                      If you edited this file in Microsoft Word, use the browser upload area or the Word add-in Save to DMS flow to create the official EdgeLex DMS version.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Display */}
            {error && (
              <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <div className="flex items-start space-x-2">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={handleCheckin}
              disabled={isProcessing || (canCreateVersionFromWorkingCopy && checkinOption === 'create-version' && !versionComment.trim())}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-lex-accent text-base font-medium text-lex-accent-contrast hover:bg-lex-accent-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-lex-accent sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Checking in...
                </>
              ) : (
                <>
                  <CheckIcon className="h-4 w-4 mr-2" />
                  {canCreateVersionFromWorkingCopy ? 'Check In' : 'Release Checkout'}
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={isProcessing}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-lex-accent sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Use portal to render modal at document body level
  return createPortal(modalContent, document.body);
}

export default CheckinModal; 
